import { DateTime, Interval } from "luxon";
import { setIsAuthenticated } from "modules/user/userSlice";
import { useAppDispatch } from "utils/hooks";
import { jwtDecode } from "jwt-decode";
import { userTokenSchema, type UserToken } from "./tokenSchema";
import { ZodError } from "zod";
import { logoutIfTokenExpired } from "modules/user/userHooks";

const CLOCK_SKEW_SECONDS = 15;

const getJWTFromLocalStorage = (): string => {
  const JWT = localStorage.getItem("JWT") ?? sessionStorage.getItem("JWT");
  return JWT ?? "";
};

export const clearJWTFromStorage = () => {
  localStorage.removeItem("JWT");
  sessionStorage.removeItem("JWT");
  sessionStorage.removeItem("facilityId");
};

export const jwtHasValidExpiration = (token: string): boolean => {
  if (!token) return false;

  const parsedToken = getConvertToUserToken(token);
  if (!parsedToken) return false;

  const now = DateTime.now();

  const expSkewed = DateTime.fromSeconds(parsedToken.exp).plus({
    seconds: CLOCK_SKEW_SECONDS,
  });

  const nbfSkewed = DateTime.fromSeconds(parsedToken.nbf).minus({
    seconds: CLOCK_SKEW_SECONDS,
  });

  const validityWindow = Interval.fromDateTimes(nbfSkewed, expSkewed);

  return validityWindow.contains(now);
};

const parseUserTokenOrThrow = (token: string): UserToken => {
  const decoded: unknown = jwtDecode(token);
  return userTokenSchema.parse(decoded);
};

export const getConvertToUserToken = (token: string): UserToken | undefined => {
  if (!token) return undefined;
  try {
    return parseUserTokenOrThrow(token);
  } catch (error) {
    if (error instanceof ZodError) {
      console.error("Token validation failed:", error.issues);
    } else {
      console.error("Token decoding failed:", error);
    }
    return undefined;
  }
};

export function SetUserIsAuthenticatedValue(value: boolean) {
  const dispatch = useAppDispatch();
  dispatch(setIsAuthenticated({ isAuthenticated: value }));
}

export function getCurrentUserToken(): UserToken | undefined {
  const rawToken = getJWTFromLocalStorage();
  if (!rawToken) return;
  const isExpired = logoutIfTokenExpired(rawToken);
  return isExpired ? undefined : getConvertToUserToken(rawToken);
}

export function getCurrentJWT(): string {
  const rawToken = getJWTFromLocalStorage();
  if (!rawToken) return "";
  const isExpired = logoutIfTokenExpired(rawToken);
  return isExpired ? "" : (rawToken ?? "");
}
